import {useEffect, useMemo, useRef} from 'react';

import {Period} from '@deviceinsight/ng-ui-components';

import {KpiAggregatedChartWidgetConfigSettings, KpiAggregatedChartWidgetProps} from './types';
import ChartWidgetContent from '../common/components/ChartWidgetContent';
import useMultipleKpiValues, {KpiValueDescriptor} from '../common/hooks/useMultipleKpiValues';
import {AggregationSettings, ChartData, KpiWidgetItem} from '../common/types';
import {createChart, updateChart} from './chartUtils';
import useTimer from './useTimer';
import {OutdatedConfigError} from '../common/components/kpiWidgetErrors';
import useAssetsFromConfig from '../common/hooks/useAssetsFromConfig';
import {nonNull} from '../../../../utils/tsUtils';

const createAggregationSettings = (
	settings: KpiAggregatedChartWidgetConfigSettings,
	dashboardPeriod: Period | null | undefined
): AggregationSettings => ({
	items: settings.items
		.map(({assetUrn, kpiName}) =>
			assetUrn && kpiName
				? {
						assetUrn,
						kpiName
				  }
				: null
		)
		.filter(nonNull),
	aggregationType: settings.aggregationType,
	groupBy: settings.groupBy,
	query: {
		periodTimeframe: dashboardPeriod ? 'CUSTOM' : settings.periodTimeframe,
		periodType: settings.periodType,
		periodCount: settings.periodCount,
		customPeriodFrom: dashboardPeriod ? dashboardPeriod.tsStart : settings.customPeriodFrom,
		customPeriodTo: dashboardPeriod ? dashboardPeriod.tsEnd : settings.customPeriodTo,
		workingShiftEnabled: settings.workingShiftEnabled,
		workingShiftDays: settings.workingShiftDays,
		workingShiftTimeFrom: settings.workingShiftTimeFrom,
		workingShiftTimeTo: settings.workingShiftTimeTo
	}
});

const createChartData = (
	items: KpiWidgetItem[],
	values: KpiValueDescriptor[],
	contextAssetUrn: string | null | undefined
): ChartData[] =>
	items
		.map(({kpiName, assetUrn: itemAssetUrn, ...rest}: KpiWidgetItem) => {
			const assetUrn = contextAssetUrn || itemAssetUrn;
			const valueDescriptor = values.find(
				(valueDescriptor) => valueDescriptor.assetUrn === assetUrn && valueDescriptor.kpiName === kpiName
			);

			if (kpiName && assetUrn) {
				const chartDataItems: ChartData = {
					kpiName,
					assetUrn,
					facts: valueDescriptor?.facts,
					...rest
				};
				return chartDataItems;
			}

			return null;
		})
		.filter(nonNull);

const KpiAggregatedChartWidget = ({
	context,
	config: {key, settings, title},
	editMode,
	dashboardTime,
	dashboardPeriod
}: KpiAggregatedChartWidgetProps) => {
	const {periodTimeframe, items, groupBy} = settings;
	const aggregationSettings = useMemo(
		() => createAggregationSettings(settings, dashboardPeriod),
		[dashboardPeriod, settings]
	);
	const {values, fetching, errorMessage, lastUpdate, reload} = useMultipleKpiValues(
		context,
		aggregationSettings,
		key,
		dashboardTime,
		editMode,
		true
	);
	useTimer(dashboardTime, groupBy, lastUpdate, reload);
	const chartDataSets = useMemo<ChartData[]>(
		() => createChartData(items, values, context.type === 'asset' ? context.asset.urn : undefined),
		[items, values, context]
	);
	const containerRef = useRef(null);
	const assetGroupContext = context.type === 'assetGroup';
	const chartRef = useRef(null);
	const assets = useAssetsFromConfig(items, context);
	useEffect(() => {
		if (!containerRef.current) {
			return;
		}

		const chart = chartRef.current;

		if (!editMode) {
			if (chart && chartDataSets.length > 0) {
				updateChart(chart, chartDataSets, assetGroupContext, settings, aggregationSettings, lastUpdate, assets);
			} else {
				chartRef.current = createChart(
					containerRef.current,
					chartDataSets,
					settings,
					aggregationSettings,
					assetGroupContext,
					lastUpdate,
					assets
				);
			}
		}
	}, [assetGroupContext, assets, chartDataSets, editMode, settings, lastUpdate, aggregationSettings]);
	const kpiNames: string = settings.items.map((item) => item.kpiName).join(', ');

	if (periodTimeframe) {
		return (
			<ChartWidgetContent
				loading={fetching}
				title={title}
				kpiErrorMessage={errorMessage}
				kpiNames={kpiNames}
				containerRef={containerRef}
				editMode={editMode}
			/>
		);
	} else {
		return <OutdatedConfigError />;
	}
};

export default KpiAggregatedChartWidget;
